/**
 * «Юниверситет» (28.09): карта комнат (SVG, как в макете). Состояния выводятся из
 * прогресса (roomStates), а не захардкожены. Узлы — кнопки: Tab, Enter/Space
 * (в макете tabindex был, а клавиши не обрабатывались). Линии: соседние комнаты
 * path, от последней — пунктир к секретной; горит, если A пройдена, а B пройдена/сейчас.
 */
import type { KeyboardEvent } from "react";
import type { RoomState, UniPack, UniRoom } from "@ub/shared/uni";
import css from "./uni.module.css";

const W = 110;
const H = 54;

const STATUS: Record<RoomState, string> = { done: "ПРОЙДЕНО", cur: "СЕЙЧАС", secret: "???", lock: "ЗАКРЫТО" };
const NODE: Record<RoomState, string | undefined> = { done: css.nDone, cur: css.nCur, secret: css.nSecret, lock: css.nLock };

interface MapProps {
  pack: UniPack;
  states: Record<string, RoomState>;
  onOpen: (roomId: string) => void;
}

export function MapCard({ pack, states, onOpen }: MapProps) {
  const byId = new Map(pack.rooms.map((r) => [r.id, r]));
  const doneN = pack.path.filter((id) => states[id] === "done").length;

  const xs = pack.rooms.map((r) => r.map[0]);
  const ys = pack.rooms.map((r) => r.map[1]);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  const viewBox = `${minX - 10} ${minY - 10} ${Math.max(...xs) + W - minX + 20} ${Math.max(...ys) + H - minY + 20}`;

  const links: [UniRoom, UniRoom, boolean][] = [];
  for (let i = 1; i < pack.path.length; i++) {
    const a = byId.get(pack.path[i - 1] ?? ""), b = byId.get(pack.path[i] ?? "");
    if (a && b) links.push([a, b, false]);
  }
  const last = byId.get(pack.path[pack.path.length - 1] ?? "");
  if (last) for (const r of pack.rooms) if (r.secret && !pack.path.includes(r.id)) links.push([last, r, true]);

  const key = (id: string) => (e: KeyboardEvent<SVGGElement>) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(id); }
  };

  return (
    <section className={`${css.card} ${css.map}`} aria-labelledby="uni-map-h" data-fb="map" data-fb-name="Карта">
      <h3 className={css.h} id="uni-map-h">Карта · {pack.title} <b>{doneN} / {pack.path.length}</b></h3>
      <svg viewBox={viewBox} role="group" aria-label="Карта помещений">
        {links.map(([a, b, dashed]) => {
          const sa = states[a.id], sb = states[b.id];
          const lit = sa === "done" && (sb === "done" || sb === "cur");
          return (
            <line
              key={`${a.id}-${b.id}`}
              className={`${css.path} ${lit ? css.lit : ""}`}
              strokeDasharray={dashed ? "4 4" : undefined}
              x1={a.map[0] + W / 2} y1={a.map[1] + H / 2}
              x2={b.map[0] + W / 2} y2={b.map[1] + H / 2}
            />
          );
        })}
        {pack.rooms.map((r) => {
          const st = states[r.id] ?? "lock";
          const label = st === "lock" && r.boss ? "БОСС" : STATUS[st];
          const [x, y] = r.map;
          return (
            <g
              key={r.id}
              className={`${css.node} ${NODE[st] ?? ""} ${r.boss ? css.nBoss : ""}`}
              tabIndex={0}
              role="button"
              aria-label={`${r.name}: ${st === "secret" ? "секретная комната" : label.toLowerCase()}`}
              onClick={() => onOpen(r.id)}
              onKeyDown={key(r.id)}
              data-fb={`room:${r.id}`} data-fb-name={`Комната «${r.name}» на карте`}
            >
              <rect x={x} y={y} width={W} height={H} rx={9} />
              <text x={x + W / 2} y={y + 22}>{r.name}</text>
              <text className={css.st} x={x + W / 2} y={y + 40}>{label}</text>
            </g>
          );
        })}
      </svg>
    </section>
  );
}
